<template>
  <SfMarkdownEditor
    v-model="markdownModel"
    class="tw-h-[200px]"
    :mention-usernames="mentionUsernames"
    :mention-accounts="mentionAccounts"
    :mention-recon-templates="mentionReconTemplates"
    :is-loading-accounts="isLoadingAccounts"
    :is-loading-recon-templates="isLoadingReconTemplates"
    :pulse-usernames="pulseUsernames"
    :pulse-warning-active="periodPrivate"
    :square-bottom="squareBottom"
    :placeholder="placeholder"
  />
  <!-- Round the stacked hints' bottom corners as one block. -->
  <div class="tw-overflow-hidden tw-rounded-b-lg">
    <RemarkMentionsHint
      :text="markdownModel ?? ''"
      :company-id="companyId"
      @hint-visibility-changed="(v) => updateHintVisibility('mentions', v)"
    />
    <PulseMentionHint
      :text="markdownModel ?? ''"
      :pulse-usernames="pulseUsernames"
      :period-private="periodPrivate"
      :period-name="periodName"
      @hint-visibility-changed="(v) => updateHintVisibility('pulse', v)"
    />
  </div>
  <div v-if="hasErrors" class="sf-input-errors">
    <SfInputError v-for="e in errors" :key="e" class="tw-mt-1" :text="e" />
  </div>
</template>
<script lang="ts" setup>
import { useVModel } from '@vueuse/core'
import { computed, reactive, watch } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'
import { SfInputError } from '@silverfin/ui-components'
import RemarkMentionsHint from './remark-mentions-hint.vue'
import PulseMentionHint from './pulse-mention-hint.vue'
import { SfMarkdownEditor } from '../sf-markdown-editor'
import type { MentionItem } from '../sf-markdown-editor/types'

interface Props {
  modelValue: string | null | undefined
  errors?: string[]
  companyId?: number
  periodName?: string
  periodPrivate?: boolean
  mentionUsernames: string[]
  pulseUsernames?: string[]
  mentionAccounts: MentionItem[]
  mentionReconTemplates?: MentionItem[]
  isLoadingAccounts?: boolean
  isLoadingReconTemplates?: boolean
  squareBottom: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  errors: () => [],
  mentionUsernames: () => [],
  pulseUsernames: () => [],
  mentionAccounts: () => [],
  mentionReconTemplates: () => [],
  isLoadingAccounts: false,
  isLoadingReconTemplates: false,
  companyId: undefined,
  periodName: '',
  periodPrivate: false,
  squareBottom: false,
})

const emit =
  defineEmits<{
    'update:modelValue': [value: string]
    'hint-visibility-changed': [value: boolean]
  }>()

const hintVisibility = reactive({ mentions: false, pulse: false })

function updateHintVisibility(hint: 'mentions' | 'pulse', value: boolean) {
  hintVisibility[hint] = value
}

watch(
  () => hintVisibility.mentions || hintVisibility.pulse,
  (anyVisible) => emit('hint-visibility-changed', anyVisible),
  { immediate: true }
)

const markdownModel = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

const I18n = useI18n()

const hasErrors = computed(() => {
  return props.errors && props.errors.length > 0
})

const placeholder = I18n.t('communication.remark_form.text_placeholder')
</script>
